
import { useState } from "react";
import { Menu, X, LogOut, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleAuthClick = () => {
    navigate('/auth');
  };

  const handleBecomeSupplierClick = () => {
    const joinWaitlistSection = document.getElementById('join-waitlist');
    if (joinWaitlistSection) {
      joinWaitlistSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className="bg-white/95 backdrop-blur-sm shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo - mobile-optimized */}
          <div className="flex-shrink-0">
            <Link to="/">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent">
                ZimKonnect
              </h1>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-4 lg:space-x-6 xl:space-x-8 items-center" aria-label="Main site navigation">
            <a 
              href="#home" 
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200 text-sm lg:text-base font-medium"
              aria-label="Go to ZimKonnect home section"
            >
              Home
            </a>
            <a 
              href="#how-it-works" 
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200 text-sm lg:text-base font-medium"
              aria-label="Learn how Zimbabwe diaspora project tracking works"
            >
              How It Works
            </a>
            <Link 
              to="/blog" 
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200 text-sm lg:text-base font-medium"
              aria-label="Read ZimKonnect blog about Zimbabwe diaspora projects"
            >
              Blog
            </Link>
            <a 
              href="#join-waitlist" 
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200 text-sm lg:text-base font-medium"
              aria-label="Join Zimbabwe diaspora project tracking waitlist"
            >
              Join Waitlist
            </a>
            {user ? (
              <div className="flex items-center space-x-2 lg:space-x-3">
                <div className="flex items-center space-x-2">
                  <User size={14} aria-hidden="true" />
                  <span className="text-xs lg:text-sm text-gray-600 max-w-24 lg:max-w-32 truncate">{user.email}</span>
                </div>
                <Button
                  onClick={handleSignOut}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-1 lg:space-x-2 min-h-[36px] lg:min-h-[40px] text-xs lg:text-sm px-2 lg:px-3"
                  aria-label="Sign out of ZimKonnect account"
                >
                  <LogOut size={14} aria-hidden="true" />
                  <span className="hidden lg:inline">Sign Out</span>
                </Button>
              </div>
            ) : (
              <>
                <Button
                  onClick={handleAuthClick}
                  variant="outline"
                  size="sm"
                  className="min-h-[36px] lg:min-h-[40px] text-xs lg:text-sm px-3 lg:px-4"
                  aria-label="Sign in to ZimKonnect"
                >
                  Sign In
                </Button>
                <button 
                  onClick={handleBecomeSupplierClick}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-2 lg:px-3 xl:px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105 text-xs lg:text-sm xl:text-base min-h-[36px] lg:min-h-[40px] font-medium"
                  aria-label="Become a verified Zimbabwe supplier"
                >
                  <span className="hidden lg:inline">Become a Supplier</span>
                  <span className="lg:hidden">Supplier</span>
                </button>
              </>
            )}
          </nav>

          {/* Mobile menu button - improved touch target */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-blue-600 transition-colors duration-200 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation rounded-lg hover:bg-gray-100"
              aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation - enhanced for touch */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 py-4 animate-fade-in">
            <nav className="flex flex-col space-y-1" aria-label="Mobile navigation">
              <a 
                href="#home" 
                className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 px-4 py-3 rounded-lg min-h-[48px] flex items-center touch-manipulation font-medium"
                aria-label="Go to ZimKonnect home section"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </a>
              <a 
                href="#how-it-works" 
                className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 px-4 py-3 rounded-lg min-h-[48px] flex items-center touch-manipulation font-medium"
                aria-label="Learn how Zimbabwe diaspora project tracking works"
                onClick={() => setIsMenuOpen(false)}
              >
                How It Works
              </a>
              <Link 
                to="/blog" 
                className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 px-4 py-3 rounded-lg min-h-[48px] flex items-center touch-manipulation font-medium"
                aria-label="Read ZimKonnect blog about Zimbabwe diaspora projects"
                onClick={() => setIsMenuOpen(false)}
              >
                Blog
              </Link>
              <a 
                href="#join-waitlist" 
                className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 px-4 py-3 rounded-lg min-h-[48px] flex items-center touch-manipulation font-medium"
                aria-label="Join Zimbabwe diaspora project tracking waitlist"
                onClick={() => setIsMenuOpen(false)}
              >
                Join Waitlist
              </a>
              {user ? (
                <div className="px-4 pt-2 space-y-3">
                  <div className="flex items-center space-x-2 text-sm text-gray-600 py-2 bg-gray-50 rounded-lg px-3">
                    <User size={16} aria-hidden="true" />
                    <span className="truncate font-medium">{user.email}</span>
                  </div>
                  <Button
                    onClick={handleSignOut}
                    variant="outline"
                    size="sm"
                    className="w-full flex items-center justify-center space-x-2 min-h-[48px] touch-manipulation font-medium"
                    aria-label="Sign out of ZimKonnect account"
                  >
                    <LogOut size={16} aria-hidden="true" />
                    <span>Sign Out</span>
                  </Button>
                </div>
              ) : (
                <div className="px-4 pt-2 space-y-3">
                  <Button
                    onClick={() => {
                      handleAuthClick();
                      setIsMenuOpen(false);
                    }}
                    variant="outline"
                    size="sm"
                    className="w-full min-h-[48px] touch-manipulation font-medium"
                    aria-label="Sign in to ZimKonnect"
                  >
                    Sign In
                  </Button>
                  <button 
                    onClick={() => {
                      handleBecomeSupplierClick();
                      setIsMenuOpen(false);
                    }}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 rounded-lg w-full text-center hover:from-blue-700 hover:to-blue-800 transition-all duration-200 min-h-[48px] touch-manipulation font-medium"
                    aria-label="Become a verified Zimbabwe supplier"
                  >
                    Become a Supplier
                  </button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
